import { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, TrendingUp, Users, DollarSign } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PartnerApplicationForm from "@/components/vendor/PartnerApplicationForm";
import { useAuth } from "@/contexts/AuthContext";
import { requireUser } from "@/lib/requireUser";

const Sell = () => {
  const [applicationModalOpen, setApplicationModalOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleApplyClick = () => {
    requireUser({
      user,
      navigate,
      currentPath: '/sell',
      action: () => setApplicationModalOpen(true),
    });
  };
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 to-accent/10">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Partner with Circle Network
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Reach 50,000+ real estate agents actively seeking the tools and services you offer
            </p>
            <Button size="lg" className="font-semibold text-lg px-8" onClick={handleApplyClick}>
              Apply to become a partner
            </Button>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Why partner with us?</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-6">
                <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3">Targeted agent audience</h3>
                <p className="text-muted-foreground">
                  Connect with real estate professionals actively searching for CRMs, coaching, marketing services, and business tools
                </p>
              </Card>

              <Card className="p-6">
                <div className="rounded-full bg-accent/10 w-12 h-12 flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-3">Predictable growth</h3>
                <p className="text-muted-foreground">
                  Reach agents at the moment they're comparing options and ready to invest in their business
                </p>
              </Card>

              <Card className="p-6">
                <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mb-4">
                  <DollarSign className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3">Simple partnership</h3>
                <p className="text-muted-foreground">
                  Transparent revenue share model with no listing fees or upfront costs to join
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">How it works</h2>
            
            <div className="max-w-3xl mx-auto space-y-8">
              <div className="flex gap-4">
                <div className="rounded-full bg-primary text-primary-foreground w-8 h-8 flex items-center justify-center font-bold shrink-0">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Apply to partner</h3>
                  <p className="text-muted-foreground">
                    Submit your application and tell us about your service, tool, or coaching program. We'll review and get back to you within 5 business days.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="rounded-full bg-primary text-primary-foreground w-8 h-8 flex items-center justify-center font-bold shrink-0">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Create your listing</h3>
                  <p className="text-muted-foreground">
                    Work with our team to create a compelling listing that showcases how you help agents grow their business.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="rounded-full bg-primary text-primary-foreground w-8 h-8 flex items-center justify-center font-bold shrink-0">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Launch and grow</h3>
                  <p className="text-muted-foreground">
                    Your deal goes live to our audience. Track performance, manage customers, and watch your revenue grow.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Success stories */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Partner success stories</h2>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <Card className="p-6">
                <div className="mb-4">
                  <div className="text-4xl font-bold text-primary mb-2">$180K+</div>
                  <p className="text-sm text-muted-foreground">in Agent Revenue</p>
                </div>
                <p className="italic text-muted-foreground mb-4">
                  "Circle Network connected us with 300+ agents in our first quarter. The quality of leads and conversion rates beat every other channel."
                </p>
                <p className="font-semibold">— Luxury Presence, Marketing Platform</p>
              </Card>

              <Card className="p-6">
                <div className="mb-4">
                  <div className="text-4xl font-bold text-accent mb-2">800+</div>
                  <p className="text-sm text-muted-foreground">Coaching Clients</p>
                </div>
                <p className="italic text-muted-foreground mb-4">
                  "Circle Network helped us scale our agent coaching program from regional to national. We now serve agents in 35+ states."
                </p>
                <p className="font-semibold">— Workman Success Systems, Coaching</p>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-foreground text-background">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to reach more agents?</h2>
            <p className="text-xl mb-8 text-background/80">
              Join 50+ real estate service providers already partnering with Circle Network
            </p>
            <Button size="lg" className="font-semibold text-lg px-8" onClick={handleApplyClick}>
              Apply now
            </Button>
          </div>
        </section>
      </main>

      <Dialog open={applicationModalOpen} onOpenChange={setApplicationModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Partner Application</DialogTitle>
            <DialogDescription>
              Complete the application to become a Circle Network vendor partner
            </DialogDescription>
          </DialogHeader>
          <PartnerApplicationForm onSuccess={() => setApplicationModalOpen(false)} />
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default Sell;
